/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { shallowEqual } from 'react-redux';

import { useAppDispatch, useAppSelector } from '../../../store';
import { TELEPHONY } from '../components/phone/constants';
import { FIELDS } from '../components/phone/schema';
import {
  listSpeechToTextInstanceUrl,
  listTextToSpeechInstanceUrl,
  loadSpeechToTextInstanceInCpd,
  loadTextToSpeechInstanceInCpd
} from '../store/voice/actions';
import {
  selectDefaultSpeechToTextModel,
  selectModelsFilteredByAssistantLanguage,
  selectSpeechToTextInstanceIsLoading,
  selectSttUrl,
  selectTextToSpeechVoices,
  selectTtsUrl
} from '../store/voice/selectors';
import { useFormHelpers } from './useFormHelpers';

export const useSpeechInCpd = (type: 'STT' | 'TTS') => {
  const isSTT = type === 'STT';

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);
  const dispatch = useAppDispatch();

  // get previously saved values
  const filtered =
    getFieldValue(FIELDS.STT.LANGUAGE_SELECTIONS_FILTER_TOGGLE) !== 'NO';
  const instanceId =
    getFieldValue(isSTT ? FIELDS.STT.INSTANCE_ID : FIELDS.TTS.INSTANCE_ID) ??
    '';
  const token =
    getFieldValue(isSTT ? FIELDS.STT.BEARER_TOKEN : FIELDS.TTS.BEARER_TOKEN) ??
    '';
  const sttModel = FIELDS.STT.NARROWBAND.MODEL;
  const prevSavedModel = getFieldValue(sttModel);
  const defaultValueModel = useAppSelector(selectDefaultSpeechToTextModel);
  const loading = useAppSelector(selectSpeechToTextInstanceIsLoading);
  const models = useAppSelector(
    selectModelsFilteredByAssistantLanguage,
    shallowEqual
  );
  const voices = useAppSelector(selectTextToSpeechVoices, shallowEqual);
  const previousUrl =
    getFieldValue(isSTT ? FIELDS.STT.URL : FIELDS.TTS.URL) ?? '';
  const sttUrl = useAppSelector(selectSttUrl);
  const ttsUrl = useAppSelector(selectTtsUrl);

  // update url in fields when sttUrl changes
  useEffect(() => {
    if (previousUrl !== sttUrl) {
      updateFieldValue(FIELDS.STT.URL, sttUrl);
    }
  }, [previousUrl, sttUrl, updateFieldValue]);

  //update url in fields when ttsUrl changes
  useEffect(() => {
    if (previousUrl !== ttsUrl) {
      updateFieldValue(FIELDS.TTS.URL, ttsUrl);
    }
  }, [previousUrl, ttsUrl, updateFieldValue]);

  useEffect(() => {
    // if there is no previously saved model, save the default model
    if (!prevSavedModel) {
      updateFieldValue(sttModel, defaultValueModel);
    }
  }, [defaultValueModel, prevSavedModel, sttModel, updateFieldValue]);

  // update value for toggle in STT
  const updateIsFiltered = (isFiltered: boolean) => {
    updateFieldValue(
      FIELDS.STT.LANGUAGE_SELECTIONS_FILTER_TOGGLE,
      isFiltered ? 'YES' : 'NO'
    );
  };

  // update instance id
  const updateInstanceId = (instanceId: string) => {
    updateFieldValue(
      isSTT ? FIELDS.STT.INSTANCE_ID : FIELDS.TTS.INSTANCE_ID,
      instanceId
    );
  };

  // generate stt instance url
  const getSttInstanceUrl = () => {
    if (instanceId) {
      dispatch(listSpeechToTextInstanceUrl(instanceId));
    }
  };

  // generate tts instance url
  const getTtsInstanceUrl = () => {
    if (instanceId) {
      dispatch(listTextToSpeechInstanceUrl(instanceId));
    }
  };

  // fetch models for STT
  const handleFetchModels = () => {
    dispatch(loadSpeechToTextInstanceInCpd(instanceId));
  };

  //fetch voices for TTS
  const handleFetchVoices = () => {
    dispatch(loadTextToSpeechInstanceInCpd(instanceId));
  };

  // Compare against original to determine if dirty
  const handleSttModelChange = selectedItem => {
    // set narrowband model since broadband is deprecated
    updateFieldValue(FIELDS.STT.BAND_PREFERENCE, TELEPHONY);
    updateFieldValue(FIELDS.STT.NARROWBAND.MODEL, selectedItem.name);
  };

  // update bearer token
  const updateBearerToken = (bearerToken: string): void => {
    updateFieldValue(
      isSTT ? FIELDS.STT.BEARER_TOKEN : FIELDS.TTS.BEARER_TOKEN,
      bearerToken
    );
  };

  return {
    defaultValueModel,
    filtered,
    getSttInstanceUrl,
    getTtsInstanceUrl,
    handleFetchModels,
    handleFetchVoices,
    handleSttModelChange,
    instanceId,
    loading,
    models,
    sttModel,
    token,
    updateBearerToken,
    updateInstanceId,
    updateIsFiltered,
    voices
  };
};
